import { injectable } from 'inversify';
import { v4 as uuidv4 } from 'uuid';

import { BraceOrderStatus } from '@modulate/shared/src/constants/brace-order-status';
import { BraceType } from '@modulate/shared/src/constants/brace-type';
import { OpeningType } from '@modulate/shared/src/constants/opening-type';
import { IBraceInfo, IBraceOrder } from '@modulate/shared/src/models/brace-order';
import { IDataQueryResponse } from '@modulate/shared/src/models/data-query';
import { IFileUploaded } from '@modulate/shared/src/models/file';
import { IPatientInfo } from '@modulate/shared/src/models/patient';
import api from '../axios-config';

export const BRACE_ORDERS_SERVICE = Symbol.for('BRACE_ORDERS_SERVICE');

export interface IBraceOrdersService {
  create(braceOrder: IBraceOrder): Promise<IBraceOrder>;
  getById(id: string): Promise<IBraceOrder>;
  getCountForPatient(patientId: string, clinicId: number): Promise<number>;
  findAllForPatient(patientId: string, clinicId: number): Promise<IBraceOrder[]>;
}

@injectable()
export class BraceOrdersService implements IBraceOrdersService {
  async findAllForPatient(patientId: string, clinicId: number): Promise<IBraceOrder[]> {
    const response = await api.get<IDataQueryResponse<any>>(`/api/${clinicId}/${patientId}/braces`);
    return this.transformResponseToBraceOrders(response.data);
  }

  async create(braceOrder: IBraceOrder): Promise<IBraceOrder> {
    const response = await api.post<IBraceOrder>('/api/braces', braceOrder);
    return response.data;
  }

  async getById(id: string): Promise<IBraceOrder> {
    const response = await api.get<IBraceOrder>(`/api/braces/${id}`);
    if (!response) {
      throw new Error('No brace order is associated with the ID specified.');
    }
    return response.data;
  }

  async getCountForPatient(patientId: string, clinicId: number): Promise<number> {
    const response = await api.get(`/api/${clinicId}/${patientId}/braces`);
    if (!response) {
      throw new Error('No brace order is associated with the ID specified.');
    }
    // Endpoint returns { data, totalCount }
    return (response.data as { totalCount: number }).totalCount;
  }

  private transformResponseToBraceOrders(response: IDataQueryResponse<any>): IBraceOrder[] {
    return response.data.map((apiBrace: any, index: number) => {
      const radiographUrls: string[] =
        typeof apiBrace.radiograph === 'string' && apiBrace.radiograph.length > 0
          ? apiBrace.radiograph
              .split(',')
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0)
          : [];
      const scanUrls: string[] =
        typeof apiBrace.scan === 'string' && apiBrace.scan.length > 0
          ? apiBrace.scan
              .split(',')
              .map((s: string) => s.trim())
              .filter((s: string) => s.length > 0)
          : [];
      return {
        id: apiBrace.id.toString(),
        status: apiBrace.status as BraceOrderStatus,
        patientInfo: {
          patientId: apiBrace.patient_id.toString(),
          height: apiBrace.patient_height,
          weight: apiBrace.patient_weight,
        } as IPatientInfo,
        braceInfo: {
          name: `Brace ${index + 1}`, // Assuming there's no name in the response, set a default or transform as needed
          date: new Date(apiBrace.date_ordered),
          type: apiBrace.type as BraceType,
          hoursPerDay: apiBrace.hours_per_day,
          opening: apiBrace.opening as OpeningType,
        } as IBraceInfo,
        prescriberName: apiBrace.prescriber,
        clinicName: apiBrace.hospital,
        prescriptionFiles: [], // Assuming these need to be fetched or are part of another service
        radiographyFiles: this.transformFiles(radiographUrls),
        scansFiles: this.transformFiles(scanUrls),
      };
    });
  }
  private transformFiles(fileUrls: string[]): IFileUploaded[] {
    return fileUrls.map((url) => ({
      id: uuidv4(), // Set this if you have an ID from the API
      name: this.extractFileName(url), // Extract or set a default name
      type: this.extractFileType(url), // Extract or set a default type
      url: url,
      status: undefined, // Set this if you have a status from the API
    }));
  }

  private extractFileName(url: string): string {
    // Extract the file name from the URL or set a default name
    return url.split('/').pop() || 'default-name';
  }

  private extractFileType(url: string): string {
    // Extract the file type from the URL or set a default type
    const extension = url.split('.').pop();
    return extension ? `image/${extension}` : 'application/octet-stream';
  }
}
